import { Calendar, type Options } from "vanilla-calendar-pro";
import type { Plugin } from "../../core/plugin";
import { PluginRegistry } from "../../core/pluginRegistry";

interface PopupEntry {
  label: string;
  dayLabel?: string;
}

interface ItineraryCalendarConfig {
  enableDates?: string[];
  popups?: Record<string, PopupEntry>;
  dayLabel?: string;
  calendarOptions?: Partial<Options>;
}

type ItineraryCalendarUserConfig = Partial<Options> & ItineraryCalendarConfig;

interface ParsedCalendarConfig {
  dayLabel?: string;
  popups?: Record<string, PopupEntry>;
  options: Partial<Options>;
  hasCustomLocale: boolean;
}

const POPUP_ARROW_SVG = `<div data-svg-wrapper>
  <svg width="16" height="22" viewBox="0 0 16 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 11L3.57352e-08 21.5L9.53674e-07 0.499999L16 11Z" fill="#F5F5F5"/>
  </svg>
</div>`;

const DEFAULT_LAYOUT = `
  <div class="vc-header" data-vc="header" role="toolbar" aria-label="Calendar Navigation">
    <button type="button" data-vc-arrow="prev" aria-label="Previous month">
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
        <path d="M20.4875 25.9258L10 20.0633L10 12.6133L20.4875 6.75703L21.8875 5.71953L21.8875 11.932L12.7937 16.5195L12.7937 16.1383L21.8875 20.7133L21.8875 26.957L20.4875 25.9258Z" fill="#EA5153"/>
      </svg>
    </button>
    <div class="vc-header__content" data-vc-header="content">
      <#Month />
      <#Year />
    </div>
    <button type="button" data-vc-arrow="next" aria-label="Next month">
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
        <path d="M11.5125 25.9258L22 20.0633L22 12.6133L11.5125 6.75703L10.1125 5.71953L10.1125 11.932L19.2063 16.5195L19.2063 16.1383L10.1125 20.7133L10.1125 26.957L11.5125 25.9258Z" fill="#EA5153"/>
      </svg>
    </button>
  </div>
  <div class="vc-wrapper" data-vc="wrapper">
    <#WeekNumbers />
    <div class="vc-content" data-vc="content">
      <#Week />
      <#Dates />
      <#DateRangeTooltip />
    </div>
  </div>
  <#ControlTime />
`;

export class ItineraryCalendar implements Plugin {
  private calendar: Calendar | null = null;
  private lastIsMobile: boolean;
  private hasCustomLocale = false;

  private readonly onResize = (): void => {
    if (!this.calendar) return;
    if (this.hasCustomLocale) return;
    const currentIsMobile = this.isMobile();
    if (currentIsMobile !== this.lastIsMobile) {
      this.calendar.set({ locale: this.getLocale() });
      this.lastIsMobile = currentIsMobile;
    }
  };

  constructor(private readonly element: HTMLElement) {
    this.lastIsMobile = this.isMobile();
  }

  init(): void {
    const target = this.element.querySelector(
      "[data-calendar-target]",
    ) as HTMLElement | null;

    if (!target) {
      console.warn(
        "ItineraryCalendar: Missing [data-calendar-target] element.",
      );
      return;
    }

    const config = this.parseConfig();
    this.hasCustomLocale = config.hasCustomLocale;

    const onInit = config.options.onInit;
    const onClickArrow = config.options.onClickArrow;

    this.calendar = new Calendar(target, {
      selectedTheme: "light",
      disableToday: true,
      disableAllDates: false,
      selectionMonthsMode: "only-arrows",
      selectionYearsMode: "only-arrows",
      displayDatesOutside: true,
      firstWeekday: 1,
      layouts: {
        default: DEFAULT_LAYOUT,
        ...config.options.layouts,
      },
      ...config.options,
      locale: config.options.locale ?? this.getLocale(),
      popups: {
        ...this.buildPopups(config.popups),
        ...config.options.popups,
      },
      onInit: (self) => {
        this.updatePopupDays(config.dayLabel);
        onInit?.(self);
      },
      onClickArrow: (self, event) => {
        this.updatePopupDays(config.dayLabel);
        onClickArrow?.(self, event);
      },
    });

    this.calendar.init();
    window.addEventListener("resize", this.onResize);
  }

  destroy(): void {
    window.removeEventListener("resize", this.onResize);
    this.calendar = null;
  }

  private isMobile(): boolean {
    return globalThis.window?.matchMedia("(max-width: 768px)").matches ?? false;
  }

  private getLocale() {
    const mobile = this.isMobile();
    return {
      months: {
        long: [
          "Enero",
          "Febrero",
          "Marzo",
          "Abril",
          "Mayo",
          "Junio",
          "Julio",
          "Agosto",
          "Septiembre",
          "Octubre",
          "Noviembre",
          "Diciembre",
        ],
        short: [
          "Ene",
          "Feb",
          "Mar",
          "Abr",
          "May",
          "Jun",
          "Jul",
          "Ago",
          "Sep",
          "Oct",
          "Nov",
          "Dic",
        ],
      },
      weekdays: mobile
        ? {
            short: ["D", "L", "M", "X", "J", "V", "S"],
            long: ["D", "L", "M", "X", "J", "V", "S"],
          }
        : {
            short: [
              "Domingo",
              "Lunes",
              "Martes",
              "Miércoles",
              "Jueves",
              "Viernes",
              "Sábado",
            ],
            long: [
              "Domingo",
              "Lunes",
              "Martes",
              "Miércoles",
              "Jueves",
              "Viernes",
              "Sábado",
            ],
          },
    };
  }

  private parseConfig(): ParsedCalendarConfig {
    const jsonConfig = this.parseJsonConfig();
    const referencedConfig = this.parseRefConfig();
    const mergedConfig: ItineraryCalendarUserConfig = {
      ...jsonConfig,
      ...referencedConfig,
    };

    const { dayLabel, popups, calendarOptions, ...rootCalendarOptions } =
      mergedConfig;

    const options: Partial<Options> = {
      ...(rootCalendarOptions as Partial<Options>),
      ...calendarOptions,
      enableDates:
        calendarOptions?.enableDates ??
        rootCalendarOptions.enableDates ??
        mergedConfig.enableDates ??
        [],
    };

    return {
      dayLabel,
      popups,
      options,
      hasCustomLocale: options.locale !== undefined,
    };
  }

  private parseJsonConfig(): ItineraryCalendarUserConfig {
    if (!this.element.dataset.config) return {};
    try {
      return JSON.parse(
        this.element.dataset.config,
      ) as ItineraryCalendarUserConfig;
    } catch {
      console.warn("ItineraryCalendar: Invalid data-config JSON.");
      return {};
    }
  }

  private parseRefConfig(): ItineraryCalendarUserConfig {
    const ref = this.element.dataset.configRef;
    if (!ref || globalThis.window === undefined) return {};

    const value = this.getWindowPropertyByPath(ref.trim());
    if (!value || typeof value !== "object") {
      console.warn(
        `ItineraryCalendar: data-config-ref "${ref}" does not point to a valid object.`,
      );
      return {};
    }

    return value as ItineraryCalendarUserConfig;
  }

  private getWindowPropertyByPath(path: string): unknown {
    if (!path) return undefined;

    const pathParts = path.split(".");
    let current: unknown = globalThis.window;

    for (const key of pathParts) {
      if (
        !current ||
        (typeof current !== "object" && typeof current !== "function") ||
        !(key in current)
      ) {
        return undefined;
      }

      current = (current as Record<string, unknown>)[key];
    }

    return current;
  }

  private buildPopups(
    popups?: Record<string, PopupEntry>,
  ): Record<string, { modifier: string; html: string }> {
    if (!popups) return {};

    const result: Record<string, { modifier: string; html: string }> = {};
    for (const [date, entry] of Object.entries(popups)) {
      result[date] = {
        modifier: "vc-date__popup--custom",
        html: `
          <div class="inline-flex items-center justify-start vc-popup drop-shadow-md">
            <div class="p-2.5 bg-neutral-100 justify-center items-center gap-2.5 flex w-[160px]">
              <div class="grow shrink basis-0 text-center text-[#101820] text-xs font-semibold font-['Poppins'] leading-[18px]">${entry.label}</div>
            </div>
            ${POPUP_ARROW_SVG}
          </div>
        `,
      };
    }
    return result;
  }

  private updatePopupDays(dayLabel = "Salida"): void {
    setTimeout(() => {
      const popupDates = this.element.querySelectorAll(
        ".vc-date__popup--custom",
      );
      popupDates.forEach((day) => {
        if (!day.querySelector("[data-popup-day-label]")) {
          const span = document.createElement("span");
          span.dataset.popupDayLabel = "";
          span.textContent = dayLabel;
          day.appendChild(span);
        }
      });
    }, 100);
  }
}

PluginRegistry.register("itinerary-calendar", ItineraryCalendar);
